// File: frontend/src/views/public/VerifyEmail.js

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth'; // ✅ ADD THIS
import apiService from '../../services/apiService';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import styles from './VerifyEmail.module.css';

const VerifyEmail = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { login } = useAuth(); // ✅ ADD THIS
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const correlationId = searchParams.get('correlation_id');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(3);
  
  const hasVerified = useRef(false);
  
  useEffect(() => {
    if (hasVerified.current) {
      return;
    }
    
    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  // Countdown for redirect
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/dashboard');
    }
  }, [status, countdown, navigate]);
  
  const verifyEmail = async () => {
    hasVerified.current = true;
    
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid verification token. Please check your email link.');
      return;
    }
    
    try {
      console.log('🔐 Verifying email token...', { 
        token: token.substring(0, 8) + '...',
        correlation_id: correlationId
      });
      
      const response = await apiService.verifyEmailToken({ 
        token,
        correlation_id: correlationId
      });
      
      if (response.success) {
        console.log('✅ Email verified successfully');
        
        // ✅ NEW: Use login() from useAuth hook (same as sign-in flow)
        if (response.data.auto_signed_in && response.data.tokens) {
          console.log('🔑 Auto sign-in successful, logging in user...');
          
          await login(response.data); // ✅ This handles all auth state
          
          console.log('✅ User authenticated via useAuth');
        }
        
        setStatus('success');
      } else {
        throw new Error(response.error || 'Email verification failed');
      }
      
    } catch (error) {
      console.error('❌ Email verification error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Email verification failed. Please try again.');
    }
  };
  
  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };
  
  const handleBackToHome = () => {
    navigate('/');
  };
  
  const handleContactSupport = () => {
    navigate('/contact');
  };
  
  // Verifying state
  if (status === 'verifying') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          <LoadingSpinner />
          <p className={styles.verifyingText}>Verifying your email...</p>
        </div>
      </div>
    );
  }
  
  // Success state
  if (status === 'success') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          
          <div className={styles.successIcon}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#ECFDF5"/>
              <path 
                d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" 
                stroke="#10B981" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M22 4L12 14.01L9 11.01" 
                stroke="#10B981" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          
          <h1 className={styles.title}>Email Verified!</h1>
          <p className={styles.message}>
            Your email has been successfully verified and you're now signed in. Welcome to Allegory Insurance!
          </p>
          
          <div className={styles.countdownBox}>
            <p className={styles.redirectMessage}>
              Redirecting to your dashboard in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
          
          <button 
            onClick={handleGoToDashboard}
            className={styles.primaryButton}
          >
            Go to Dashboard Now
          </button>
        </div>
      </div>
    );
  }
  
  // Error state
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.brandHeader}>
          <span className={styles.logo}>💳</span>
          <h2 className={styles.brandName}>Allegory Insurance</h2>
        </div>
        
        <div className={styles.errorIcon}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#FEF2F2"/>
            <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
            <path d="M12 8V12M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        
        <h1 className={styles.title}>Verification Failed</h1>
        <div className={styles.errorBox}>
          <p className={styles.errorMessage}>{errorMessage}</p>
        </div>
        
        <p className={styles.helpText}>
          This verification link may have expired or already been used. Please try signing in or request a new verification email.
        </p>
        
        <div className={styles.buttonGroup}>
          <button 
            onClick={handleBackToHome}
            className={styles.primaryButton}
          >
            Back to Home
          </button>
          
          <button 
            onClick={handleContactSupport}
            className={styles.secondaryButton}
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;